import React, { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'sonner';
import {
  Brain, Send, Mic, MicOff, Loader2, Bot, User, Volume2, Square,
  UtensilsCrossed, X, CheckCircle2, Flame,
  Clock, Star, ImagePlus,
} from 'lucide-react';
import { cn, API_BASE_URL } from '../../lib/utils';

/** Turn stored path `/api/uploads/...` or `/uploads/...` into full URL for <img src> */
function absUploadUrl(p) {
  if (p == null || p === '') return null;
  const s = String(p);
  if (s.startsWith('http://') || s.startsWith('https://')) return s;
  const origin = API_BASE_URL.replace(/\/api\/v1\/?$/, '');
  if (s.startsWith('/api/')) return `${origin}${s}`;
  if (s.startsWith('/')) return `${origin}/api${s}`.replace('/api/api/', '/api/');
  return `${origin}/api/${s}`;
}
import CustomerLayout from '../../components/CustomerLayout';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import AiMembershipRenewalDialog from '../../components/customer/AiMembershipRenewalDialog';
import {
  isCustomerAiMembershipActive,
  AI_MEMBERSHIP_REQUIRED_CODE,
} from '../../lib/customerMembership';
import { customerAiChatAPI } from '../../lib/customerApi';

const SOCKET_URL = API_BASE_URL.replace('/api/v1', '');

const AI_UNREAD_EVENT = 'customer:ai_chat_unread';

function broadcastAiUnreadCount(unread_count) {
  window.dispatchEvent(
    new CustomEvent(AI_UNREAD_EVENT, {
      detail: { unread_count: Math.max(0, Number(unread_count) || 0) },
    }),
  );
}

const BROWSER_STT_LANGUAGE = 'en-US';

// ── Browser TTS helper ────────────────────────────────────────────────────────
function browserSpeak(text, onEnd) {
  window.speechSynthesis.cancel();
  const utter = new SpeechSynthesisUtterance(text);
  utter.rate = 1;
  utter.onend = onEnd || null;
  window.speechSynthesis.speak(utter);
}
function browserSpeakStop() { window.speechSynthesis.cancel(); }

const PORTION_REPLIES_PREFIX = 'PORTION_REPLIES:';
/** Must match backend `PORTION_REPLY_SKIP_SENTINEL` (meal_intake_interactive.ts). */
const PORTION_SKIP_SENTINEL = '__WD_SKIP_ITEM__';

function formatPortionRepliesPreview(raw) {
  const t = String(raw || '').trim();
  if (!t.startsWith(PORTION_REPLIES_PREFIX)) return t;
  try {
    const obj = JSON.parse(t.slice(PORTION_REPLIES_PREFIX.length));
    return Object.entries(obj)
      .map(([k, v]) =>
        String(v) === PORTION_SKIP_SENTINEL ? `${k}: (skipped)` : `${k}: ${v}`,
      )
      .join(' · ');
  } catch {
    return 'Portions confirmed';
  }
}

/**
 * Shrink food photos for AI chat (Socket.IO + vision token limits).
 * @returns {Promise<{ base64: string, mime: string, previewDataUrl: string }>}
 */
function compressImageFile(file, maxEdge = 1280, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const max = maxEdge;
      if (width > max || height > max) {
        if (width >= height) {
          height = Math.round((height * max) / width);
          width = max;
        } else {
          width = Math.round((width * max) / height);
          height = max;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not available'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      const mime = 'image/jpeg';
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Compression failed'));
            return;
          }
          const reader = new FileReader();
          reader.onload = () => {
            const s = String(reader.result || '');
            const i = s.indexOf(',');
            const b64 = i >= 0 ? s.slice(i + 1) : s;
            resolve({ base64: b64, mime, previewDataUrl: s });
          };
          reader.onerror = () => reject(new Error('read failed'));
          reader.readAsDataURL(blob);
        },
        mime,
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('load failed'));
    };
    img.src = url;
  });
}

/** Human-readable local date + time for chat bubbles (ISO string or Date from API). */
function formatChatMessageTime(value) {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Multi-row portion form (assistant message type `meal_intake_question`) */
const MealIntakeQuestionForm = ({ items, locked, onSubmit, disabled }) => {
  const [sel, setSel] = useState(() => ({}));
  const [otherDraft, setOtherDraft] = useState(() => ({}));

  if (!items?.length) return null;

  const pick = (itemId, choice, item) => {
    if (locked || disabled) return;
    if (String(choice).toLowerCase() === 'other' && item.allow_other) {
      setSel((s) => ({ ...s, [itemId]: '__other__' }));
      return;
    }
    setSel((s) => ({ ...s, [itemId]: choice }));
    setOtherDraft((o) => ({ ...o, [itemId]: '' }));
  };

  const pickSkip = (itemId) => {
    if (locked || disabled) return;
    setSel((s) => ({ ...s, [itemId]: '__skip__' }));
    setOtherDraft((o) => ({ ...o, [itemId]: '' }));
  };

  const setOtherValue = (itemId, val) => {
    setOtherDraft((o) => ({ ...o, [itemId]: val }));
    // Keep sel as __other__ while typing so the input stays mounted (do not mirror text into sel).
    setSel((s) => ({ ...s, [itemId]: '__other__' }));
  };

  const canSubmit =
    !locked &&
    !disabled &&
    items.every((it) => {
      const v = sel[it.id];
      if (!v) return false;
      if (v === '__skip__') return true;
      if (v === '__other__') return String(otherDraft[it.id] || '').trim().length > 0;
      return true;
    });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const payload = {};
    for (const it of items) {
      let v = sel[it.id];
      if (v === '__skip__') {
        payload[it.label] = PORTION_SKIP_SENTINEL;
        continue;
      }
      if (v === '__other__') v = String(otherDraft[it.id] || '').trim();
      payload[it.label] = v;
    }
    onSubmit(`${PORTION_REPLIES_PREFIX}${JSON.stringify(payload)}`);
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3 pt-3 border-t border-violet-100 space-y-4 w-full max-w-full">
      {items.map((it) => (
        <div key={it.id} className="space-y-1.5">
          <p className="text-xs font-semibold text-violet-900">{it.question}</p>
          <div className="flex flex-wrap gap-2">
            {it.choices.map((c) => (
              <button
                key={c}
                type="button"
                disabled={locked || disabled}
                onClick={() => pick(it.id, c, it)}
                className={cn(
                  'text-xs font-medium px-3 py-1.5 rounded-full border transition-colors',
                  sel[it.id] && sel[it.id] !== '__other__' && sel[it.id] !== '__skip__' && sel[it.id] === c
                    ? 'bg-violet-600 border-violet-600 text-white'
                    : 'bg-card border-violet-200 text-violet-900 hover:bg-violet-50',
                  (locked || disabled) && 'opacity-60 cursor-not-allowed'
                )}
              >
                {c}
              </button>
            ))}
            <button
              type="button"
              disabled={locked || disabled}
              onClick={() => pickSkip(it.id)}
              className={cn(
                'text-xs font-medium px-3 py-1.5 rounded-full border transition-colors',
                sel[it.id] === '__skip__'
                  ? 'bg-rose-600 border-rose-600 text-white'
                  : 'bg-card border-rose-200 text-rose-800 hover:bg-rose-50',
                (locked || disabled) && 'opacity-60 cursor-not-allowed'
              )}
            >
              Skip
            </button>
          </div>
          {sel[it.id] === '__other__' && it.allow_other && (
            <input
              type="text"
              disabled={locked || disabled}
              value={otherDraft[it.id] || ''}
              onChange={(e) => setOtherValue(it.id, e.target.value)}
              placeholder={it.other_placeholder || 'Your answer'}
              className="w-full text-sm rounded-lg border border-violet-200 px-3 py-2 mt-1"
            />
          )}
        </div>
      ))}
      {!locked && (
        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="meal-intake-send-portions"
          className="w-full mt-2 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-500 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Send portions
        </button>
      )}
      {locked && (
        <p className="text-[11px] text-muted-foreground">Portions already sent.</p>
      )}
    </form>
  );
};

/** In-chat app rating (automated event: first AI use). Stays usable when newer assistant messages arrive — do not hide by “latest message” only. */
function AppRatingPrompt({ eventId, token, disabled, existingAccountRating, onRated }) {
  const [hover, setHover] = useState(0);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const saved =
    typeof existingAccountRating === 'number' &&
    existingAccountRating >= 1 &&
    existingAccountRating <= 5;

  const submit = async (stars) => {
    if (busy || done || saved || !token) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/customer/ai-chat/app-rating`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ stars, event_id: eventId || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Could not save rating');
      setSelected(stars);
      setDone(true);
      onRated?.();
      if (data.open_store && data.store_url) {
        window.open(data.store_url, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      toast.error(e.message || 'Could not save rating');
    } finally {
      setBusy(false);
    }
  };

  const filledUpTo = done && selected != null ? selected : saved ? existingAccountRating : null;
  const readOnly = saved && !done;

  return (
    <div className="mt-3 pt-3 border-t border-violet-100" data-testid="app-rating-prompt">
      <p className="text-xs text-violet-900 font-medium mb-2">
        {readOnly
          ? 'Your rating'
          : 'Tap a star to rate the app'}
      </p>
      <div className="flex gap-0.5 items-center">
        {[1, 2, 3, 4, 5].map((n) =>
          readOnly ? (
            <span key={n} className="p-1" aria-hidden>
              <Star
                className={cn(
                  'w-7 h-7 sm:w-8 sm:h-8 transition-colors',
                  filledUpTo != null && filledUpTo >= n
                    ? 'fill-amber-400 text-amber-500'
                    : 'text-violet-200',
                )}
              />
            </span>
          ) : (
            <button
              key={n}
              type="button"
              disabled={disabled || busy || done}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              onClick={() => submit(n)}
              className="p-1 rounded-lg hover:bg-violet-100 transition-colors disabled:opacity-40"
              aria-label={`${n} stars`}
            >
              <Star
                className={cn(
                  'w-7 h-7 sm:w-8 sm:h-8 transition-colors',
                  (hover >= n || (filledUpTo != null && filledUpTo >= n))
                    ? 'fill-amber-400 text-amber-500'
                    : 'text-violet-200',
                )}
              />
            </button>
          ),
        )}
      </div>
      {readOnly && (
        <p className="text-xs text-muted-foreground mt-2">Thanks for your feedback!</p>
      )}
      {done && selected != null && (
        <p className="text-xs text-emerald-600 mt-2 font-medium">
          Thanks! You rated {selected} star{selected !== 1 ? 's' : ''}.
          {selected >= 4 ? ' If the store did not open, you can rate us from the App Store or Play Store.' : ''}
        </p>
      )}
    </div>
  );
}

// ── Meal Log Card (inline in chat stream) ────────────────────────────────────
const MealLogCard = ({ food_name, estimated_calories, meal_time_name, food_image_url, created_at }) => {
  const timeLabel = formatChatMessageTime(created_at);
  const imgSrc = food_image_url ? absUploadUrl(food_image_url) : null;
  return (
    <div className="flex flex-col items-center my-3 px-4 gap-1">
      <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl px-4 py-3 max-w-[85%] shadow-sm">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt=""
            className="h-16 w-16 rounded-xl object-cover border border-emerald-200 shrink-0"
          />
        ) : (
        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
        </div>
        )}
        <div className="flex flex-col min-w-0">
          <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Meal Logged</span>
          <span className="text-sm font-medium text-emerald-900 whitespace-normal break-words">
            {food_name}
          </span>
          <span className="text-xs text-emerald-600 mt-0.5">
            {estimated_calories > 0 ? `${estimated_calories} kcal` : 'Calories not tracked'}
            {meal_time_name ? ` · ${meal_time_name}` : ''}
          </span>
        </div>
      </div>
      {timeLabel && (
        <span className="text-[10px] text-muted-foreground flex items-center gap-1 tabular-nums">
          <Clock className="w-3 h-3 shrink-0 opacity-70" aria-hidden />
          {timeLabel}
        </span>
      )}
    </div>
  );
};

function shouldHideAdjacentMealLogAck(msg, prevMsg, nextMsg) {
  if (!msg || msg.type === 'meal_log' || msg.role !== 'assistant') return false;
  if (msg.streaming) return false;
  if (msg.message_type === 'meal_log') return false;
  if (msg.interactive) return false;
  if (Array.isArray(msg.choices?.choices) && msg.choices.choices.length > 0) return false;
  return prevMsg?.type === 'meal_log' || nextMsg?.type === 'meal_log';
}

// ── Message bubble ────────────────────────────────────────────────────────────
const MessageBubble = ({
  msg,
  onPlay,
  onStop,
  playingId,
  onChoice,
  onSubmitPortions,
  portionFormLocked,
  sending,
  authToken,
  existingAccountRating,
  onAppRated,
}) => {
  if (msg.type === 'meal_log') {
    return <MealLogCard {...msg} />;
  }

  const isUser = msg.role === 'user';
  const isPlaying = playingId === msg._id;
  // Rely on structured `interactive.items` (type may be omitted when loaded from DB)
  const isMealForm = Array.isArray(msg.interactive?.items) && msg.interactive.items.length > 0;
  const isAppRating =
    msg.message_type === 'app_rating_prompt' || msg.interactive?.type === 'app_rating';
  const timeLabel = msg.streaming ? null : formatChatMessageTime(msg.created_at);

  return (
    <div className={cn('flex gap-3 mb-4', isUser ? 'flex-row-reverse' : 'flex-row')}>
      <div className={cn(
        'w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5',
        isUser ? 'bg-primary text-white' : 'bg-violet-100 text-violet-600'
      )}>
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>

      <div className={cn('flex flex-col gap-1', isUser ? 'items-end' : 'items-start')}>
        <div className={cn(
          isMealForm || isAppRating ? 'max-w-[92%] w-full sm:max-w-md' : 'max-w-[75%]',
          'rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap',
          isUser
            ? 'bg-primary text-white rounded-tr-sm'
            : 'bg-card border border-border text-foreground rounded-tl-sm shadow-sm'
        )}>
          {isUser && (msg.user_image_preview || msg.image_url) ? (
            <div className="mb-2">
              <img
                src={msg.user_image_preview || absUploadUrl(msg.image_url)}
                alt=""
                className="max-h-44 w-auto max-w-full rounded-lg object-contain border border-white/30"
              />
            </div>
          ) : null}
          {msg.content}
          {msg.streaming && !(isMealForm && msg.interactive?.items?.length) && !isAppRating && (
            <span className="inline-block w-1.5 h-4 bg-current ml-0.5 animate-pulse rounded-sm align-middle" />
          )}
          {isMealForm && Array.isArray(msg.interactive?.items) && msg.interactive.items.length > 0 && (
            <MealIntakeQuestionForm
              key={msg._id}
              items={msg.interactive.items}
              locked={portionFormLocked}
              disabled={sending}
              onSubmit={onSubmitPortions}
            />
          )}
          {isAppRating && (
            <AppRatingPrompt
              eventId={msg.interactive?.event_id}
              token={authToken}
              disabled={sending}
              existingAccountRating={existingAccountRating}
              onRated={onAppRated}
            />
          )}
        </div>

        {timeLabel && (
          <div
            className={cn(
              'flex items-center gap-1 text-[10px] text-muted-foreground tabular-nums max-w-[75%]',
              isUser ? 'flex-row-reverse pr-0.5' : 'pl-0.5',
            )}
          >
            <Clock className="w-3 h-3 shrink-0 opacity-70" aria-hidden />
            <span>{timeLabel}</span>
          </div>
        )}

        {!isUser && !msg.streaming && !isMealForm && !isAppRating && Array.isArray(msg.choices?.choices) && msg.choices.choices.length > 0 && (
          <div className="flex flex-wrap gap-2 max-w-[75%]">
            {msg.choices.choices.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onChoice?.(c, msg.choices)}
                className="text-xs font-medium bg-violet-50 border border-violet-200 text-violet-900 px-3 py-1.5 rounded-full hover:bg-violet-100 transition-colors"
              >
                {c}
              </button>
            ))}
            {msg.choices?.food_label ? (
              <button
                type="button"
                onClick={() => onChoice?.('__SKIP_ITEM__', msg.choices)}
                className="text-xs font-medium bg-rose-50 border border-rose-200 text-rose-900 px-3 py-1.5 rounded-full hover:bg-rose-100 transition-colors"
              >
                Skip
              </button>
            ) : null}
          </div>
        )}

        {!isUser && !msg.streaming && !isMealForm && (
          <button
            onClick={() => isPlaying ? onStop() : onPlay(msg)}
            title={isPlaying ? 'Stop' : 'Read aloud'}
            className={cn(
              'flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full transition-colors',
              isPlaying
                ? 'bg-rose-50 text-rose-500 border border-rose-200 hover:bg-rose-100'
                : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
            )}
          >
            {isPlaying ? <><Square className="w-2.5 h-2.5" /> Stop</> : <><Volume2 className="w-2.5 h-2.5" /> Listen</>}
          </button>
        )}
      </div>
    </div>
  );
};

// ── Typing dots ───────────────────────────────────────────────────────────────
const TypingIndicator = () => (
  <div className="flex gap-3 mb-4">
    <div className="w-8 h-8 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
      <Bot className="w-4 h-4" />
    </div>
    <div className="bg-card border border-border rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
      <div className="flex gap-1 items-center h-4">
        <span className="w-1.5 h-1.5 bg-muted-foreground/60 rounded-full animate-bounce [animation-delay:0ms]" />
        <span className="w-1.5 h-1.5 bg-muted-foreground/60 rounded-full animate-bounce [animation-delay:150ms]" />
        <span className="w-1.5 h-1.5 bg-muted-foreground/60 rounded-full animate-bounce [animation-delay:300ms]" />
      </div>
    </div>
  </div>
);

// ── Mic button ────────────────────────────────────────────────────────────────
const MicButton = ({ state, onClick }) => {
  const cls = {
    idle: 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80',
    recording: 'bg-rose-500 text-white animate-pulse',
    transcribing: 'bg-muted text-muted-foreground cursor-wait',
  }[state];

  return (
    <button
      onClick={onClick}
      disabled={state === 'transcribing'}
      title={state === 'idle' ? 'Start voice input' : state === 'recording' ? 'Stop recording' : 'Transcribing...'}
      data-testid="mic-btn"
      className={cn('w-10 h-10 rounded-xl flex items-center justify-center transition-colors shrink-0', cls)}
    >
      {state === 'transcribing'
        ? <Loader2 className="w-4 h-4 animate-spin" />
        : state === 'recording'
        ? <MicOff className="w-4 h-4" />
        : <Mic className="w-4 h-4" />}
    </button>
  );
};

// ── Calorie Progress Bar ──────────────────────────────────────────────────────
const CalorieBar = ({ consumed, target, onRefresh }) => {
  const pct = target > 0 ? Math.min(100, Math.round((consumed / target) * 100)) : 0;
  const over = consumed > target;
  return (
    <div
      className="bg-card border-b border-border px-4 py-2 flex items-center gap-3 cursor-pointer hover:bg-muted/30 transition-colors shrink-0"
      onClick={onRefresh}
      title="Click to refresh today's intake"
      data-testid="calorie-bar"
    >
      <Flame className={cn('w-3.5 h-3.5 shrink-0', over ? 'text-rose-500' : 'text-amber-500')} />
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-700', over ? 'bg-rose-400' : 'bg-emerald-400')}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[11px] text-muted-foreground font-medium shrink-0">
        {consumed} / {target} kcal today
      </span>
    </div>
  );
};

// ── Quick Log Form ────────────────────────────────────────────────────────────
const QuickLogForm = ({ mealTimes, onLog, onCancel, loading }) => {
  const [food, setFood] = useState('');
  const [calories, setCalories] = useState('');
  const [selectedMt, setSelectedMt] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!food.trim()) return;
    onLog({
      food_name: food.trim(),
      estimated_calories: calories ? Number(calories) : 0,
      meal_time_id: selectedMt?.id || null,
      meal_time_name: selectedMt?.name || 'Meal',
    });
  };

  return (
    <div className="border-t border-emerald-100 bg-emerald-50/60 px-3 py-3 shrink-0 animate-in slide-in-from-bottom-2 duration-200">
      <form onSubmit={handleSubmit}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1.5">
            <UtensilsCrossed className="w-3.5 h-3.5" />
            Quick Meal Log
          </span>
          <button type="button" onClick={onCancel} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex gap-2 mb-2">
          <input
            ref={inputRef}
            value={food}
            onChange={e => setFood(e.target.value)}
            placeholder="What did you eat? e.g. 2 chapati"
            data-testid="log-food-input"
            className="flex-1 text-sm rounded-lg border border-emerald-200 bg-card px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300/50 focus:border-emerald-400 placeholder:text-muted-foreground/60"
          />
          <input
            value={calories}
            onChange={e => setCalories(e.target.value)}
            type="number"
            min="0"
            placeholder="kcal"
            data-testid="log-calories-input"
            className="w-20 text-sm rounded-lg border border-emerald-200 bg-card px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-300/50 focus:border-emerald-400 placeholder:text-muted-foreground/60"
          />
        </div>

        {/* Meal time selector */}
        <div className="flex flex-wrap gap-1.5 mb-2">
          {mealTimes.map(mt => (
            <button
              type="button"
              key={mt.id}
              onClick={() => setSelectedMt(selectedMt?.id === mt.id ? null : mt)}
              data-testid={`meal-time-btn-${mt.name.toLowerCase().replace(/\s+/g, '-')}`}
              className={cn(
                'text-xs px-2.5 py-1 rounded-full border transition-all',
                selectedMt?.id === mt.id
                  ? 'bg-emerald-600 border-emerald-500 text-white font-medium'
                  : 'bg-card border-emerald-200 text-emerald-700 hover:bg-emerald-100'
              )}
            >
              {mt.name}
            </button>
          ))}
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!food.trim() || loading}
            data-testid="log-meal-submit-btn"
            className="flex items-center gap-1.5 text-sm bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Log It
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Today's Meals Panel ───────────────────────────────────────────────────────
const TodayPanel = ({ items, onClose }) => (
  <div className="absolute top-full right-0 z-50 mt-1 w-72 bg-card border border-border rounded-xl shadow-lg overflow-hidden">
    <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30">
      <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5" />
        Today&apos;s Log
      </span>
      <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
    {items.length === 0 ? (
      <p className="text-xs text-muted-foreground px-3 py-4 text-center">Nothing logged today yet</p>
    ) : (
      <div className="max-h-60 overflow-y-auto divide-y divide-border/50">
        {items.map((item, i) => (
          <div key={i} className="flex items-center justify-between px-3 py-2">
            <span className="text-xs text-foreground truncate flex-1">{item.food_name}</span>
            <span className="text-xs text-muted-foreground ml-2 shrink-0">
              {item.calories > 0 ? `${item.calories} kcal` : '—'}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

/** Sticky quick replies above the composer — same payload as [CHOICES] from server (or inferred). */
const QuickReplyBar = ({ meta, onSend, disabled }) => {
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherText, setOtherText] = useState('');
  if (!meta?.choices?.length) return null;

  return (
    <div
      className="border-t border-violet-200 bg-violet-50/95 px-3 py-2.5 shrink-0"
      data-testid="ai-quick-reply-bar"
    >
      <p className="text-[11px] text-violet-900 font-semibold mb-2">
        {meta.title || 'Quick reply — tap an option'}
      </p>
      <div className="flex flex-wrap gap-2">
        {meta.choices.map((c) => (
          <button
            key={c}
            type="button"
            disabled={disabled}
            onClick={() => {
              if (String(c).toLowerCase() === 'other' && meta.allow_other) {
                setOtherOpen(true);
                return;
              }
              setOtherOpen(false);
              setOtherText('');
              onSend(c);
            }}
            className="text-xs font-medium bg-card border border-violet-200 text-violet-900 px-3.5 py-2 rounded-full shadow-sm hover:bg-violet-100 hover:border-violet-300 transition-colors disabled:opacity-40"
          >
            {c}
          </button>
        ))}
        {meta.food_label ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              setOtherOpen(false);
              setOtherText('');
              onSend('__SKIP_ITEM__');
            }}
            className="text-xs font-medium bg-card border border-rose-200 text-rose-900 px-3.5 py-2 rounded-full shadow-sm hover:bg-rose-50 hover:border-rose-300 transition-colors disabled:opacity-40"
          >
            Skip
          </button>
        ) : null}
      </div>
      {otherOpen && meta.allow_other && (
        <div className="flex gap-2 mt-2.5 items-center">
          <input
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            placeholder={meta.other_placeholder || 'Your answer'}
            data-testid="ai-quick-reply-other-input"
            className="flex-1 text-sm rounded-lg border border-violet-200 bg-card px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-300/50"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && otherText.trim() && !disabled) {
                onSend(otherText.trim());
                setOtherOpen(false);
                setOtherText('');
              }
            }}
          />
          <button
            type="button"
            disabled={!otherText.trim() || disabled}
            onClick={() => {
              if (!otherText.trim()) return;
              onSend(otherText.trim());
              setOtherOpen(false);
              setOtherText('');
            }}
            className="text-xs font-medium px-3 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-40"
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function CustomerAiChatPage() {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState(false);
  const [aiTyping, setAiTyping] = useState(false);
  const [sending, setSending] = useState(false);
  /** Last assistant message choices — chips above input + under bubble */
  const [quickReply, setQuickReply] = useState(null);

  // TTS
  const [playingId, setPlayingId] = useState(null);
  const [audioEl, setAudioEl] = useState(null);
  const [serverTts, setServerTts] = useState(false);

  // STT
  const [micState, setMicState] = useState('idle');
  const [serverStt, setServerStt] = useState(false);

  // Meal logging
  const [mealTimes, setMealTimes] = useState([]);
  const [showLogForm, setShowLogForm] = useState(false);
  const [loggingMeal, setLoggingMeal] = useState(false);

  // Today's intake
  const [todayConsumed, setTodayConsumed] = useState(0);
  const [todayTarget, setTodayTarget] = useState(2000);
  const [todayItems, setTodayItems] = useState([]);
  const [showTodayPanel, setShowTodayPanel] = useState(false);

  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const socketRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const token = localStorage.getItem('customer_token');
  const { user, refreshUser } = useCustomerAuth();
  const { branding } = useBranding();
  const [showRenewalDialog, setShowRenewalDialog] = useState(false);
  /** @type {[{ base64: string, mime: string, previewDataUrl: string } | null, Function]} */
  const [pendingImage, setPendingImage] = useState(null);
  const imageFileInputRef = useRef(null);

  const aiMembershipActive = isCustomerAiMembershipActive(user);

  const openRenewalDialog = useCallback(() => setShowRenewalDialog(true), []);

  const markAiChatReadOnServer = useCallback(async () => {
    try {
      const { data } = await customerAiChatAPI.markRead();
      broadcastAiUnreadCount(data?.unread_count ?? 0);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    refreshUser?.();
  }, [refreshUser]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
  }, []);

  /** One AI session per customer — load history from server */
  const loadAiSession = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await customerAiChatAPI.getSession();
      if (data.session_id) setSessionId(data.session_id);
      const msgs = (data.messages || []).map((m, i) => ({
        ...(m.message_type === 'meal_log' || m.interactive?.type === 'meal_log'
          ? {
              _id: `hist-meal-${i}-${m.created_at || i}`,
              type: 'meal_log',
              food_name: m.interactive?.food_name || m.content,
              estimated_calories: Number(m.interactive?.estimated_calories || 0),
              meal_time_name: m.interactive?.meal_time_name || '',
              food_image_url: m.interactive?.food_image_url || null,
              created_at: m.created_at,
            }
          : {
              _id: `hist-${i}-${m.created_at || i}`,
              role: m.role,
              content: m.content,
              choices: m.choices || null,
              interactive: m.interactive,
              message_type: m.message_type,
              created_at: m.created_at,
              image_url: m.image_url || null,
              user_image_preview: m.image_url ? absUploadUrl(m.image_url) : null,
              type:
                m.message_type === 'meal_intake_question'
                  ? 'meal_intake_question'
                  : m.message_type === 'app_rating_prompt'
                    ? 'app_rating_prompt'
                    : undefined,
            }),
      }));
      setMessages(msgs);
      const lastMsg = msgs[msgs.length - 1];
      setQuickReply(
        Array.isArray(lastMsg?.choices?.choices) && lastMsg.choices.choices.length > 0
          ? lastMsg.choices
          : null
      );
      setTimeout(() => scrollToBottom(), 100);
      await markAiChatReadOnServer();
    } catch {
      // ignore
    }
  }, [token, scrollToBottom, markAiChatReadOnServer]);

  // ── Fetch helpers ─────────────────────────────────────────────────────────
  const fetchTodayIntake = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API_BASE_URL}/customer/ai-chat/today-intake`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setTodayConsumed(d.total_calories || 0);
      setTodayItems(d.items || []);
      if (d.calorie_target) setTodayTarget(d.calorie_target);
    } catch {}
  }, [token]);

  const fetchMealTimes = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API_BASE_URL}/customer/ai-chat/meal-times`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setMealTimes(d.meal_times || []);
    } catch {}
  }, [token]);

  // ── Load config + data ────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/customer/ai-chat/config`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        setServerTts(d?.tts?.enabled || false);
        setServerStt(d?.stt?.enabled || false);
      })
      .catch(() => {});
    fetchMealTimes();
    fetchTodayIntake();
  }, [token, fetchMealTimes, fetchTodayIntake]);

  useEffect(() => {
    loadAiSession();
  }, [loadAiSession]);

  const sendUserText = useCallback((raw, options = null) => {
    if (!aiMembershipActive) {
      openRenewalDialog();
      return;
    }
    const text = String(raw ?? '').trim();
    const img = options?.image;
    if ((!text && !img) || sending || !socketRef.current?.connected) return;

    const display = img
      ? (text
        ? (text.startsWith(PORTION_REPLIES_PREFIX)
          ? formatPortionRepliesPreview(text)
          : `📷 ${text}`)
        : '📷 Meal from photo')
      : (text.startsWith(PORTION_REPLIES_PREFIX)
        ? formatPortionRepliesPreview(text)
        : text);

    setQuickReply(null);
    setMessages((prev) => [...prev, {
      _id: Date.now(),
      role: 'user',
      content: display,
      user_image_preview: img?.previewDataUrl || null,
      created_at: new Date().toISOString(),
    }]);
    setInput('');
    setPendingImage(null);
    if (imageFileInputRef.current) imageFileInputRef.current.value = '';
    setSending(true);
    setAiTyping(true);
    scrollToBottom();
    const now = new Date();
    const payload = {
      session_id: sessionId || undefined,
      message: text,
      user_local_hour: now.getHours(),
      user_local_minute: now.getMinutes(),
      user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      /** Same `date` contract as add-portion / manual food — server sets DietIntake.date only when a meal is logged. */
      date: now.toISOString(),
    };
    if (img) {
      payload.image_base64 = img.base64;
      payload.image_mime_type = img.mime || 'image/jpeg';
    }
    socketRef.current.emit('ai_chat:message', payload);
  }, [aiMembershipActive, openRenewalDialog, sending, sessionId, scrollToBottom]);

  const handleSubmitPortions = useCallback((payload) => {
    sendUserText(payload);
  }, [sendUserText]);

  // ── Connect socket ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    const sock = io(`${SOCKET_URL}/ai-chat`, {
      auth: { token },
      query: { token },
      path: '/api/socket.io',
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    sock.on('connect', () => {
      setConnected(true);
      // Reload history from API so diet / scheduled AI lines appear even if
      // `ai_chat:event_messages` fired before the socket connected (or tab was in background).
      loadAiSession();
    });
    sock.on('disconnect', () => setConnected(false));
    sock.on('ai_chat:session_ready', ({ session_id }) => {
      if (session_id) setSessionId(session_id);
    });
    sock.on('ai_chat:event_messages', ({ session_id, chunks }) => {
      if (session_id) setSessionId(session_id);
      setMessages((prev) => {
        const add = [];
        for (const c of chunks || []) {
          let content = (c.text || '').trim() || ' ';
          if (c.labels?.length) {
            content += `\n\n${c.labels.map((l) => `[${l}]`).join(' ')}`;
          }
          const isRating = c.interactive?.type === 'app_rating';
          add.push({
            _id: `evt-${Date.now()}-${Math.random().toString(16).slice(2)}`,
            role: 'assistant',
            content,
            interactive: c.interactive,
            message_type: isRating ? 'app_rating_prompt' : undefined,
            type: isRating ? 'app_rating_prompt' : undefined,
            created_at: c.created_at || new Date().toISOString(),
          });
        }
        return add.length ? [...prev, ...add] : prev;
      });
      scrollToBottom();
      markAiChatReadOnServer();
    });

    sock.on('ai_chat:unread_count', ({ unread_count }) => {
      broadcastAiUnreadCount(unread_count);
    });

    sock.on('ai_chat:token', ({ token: tok }) => {
      setQuickReply(null);
      setAiTyping(false);
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.streaming) {
          const updated = [...prev];
          updated[updated.length - 1] = { ...last, content: last.content + tok };
          return updated;
        }
        return [...prev, {
          _id: Date.now(),
          role: 'assistant',
          content: tok,
          streaming: true,
          created_at: new Date().toISOString(),
        }];
      });
      scrollToBottom();
    });

    sock.on('ai_chat:done', ({ full_text, choices, interactive }) => {
      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        const meal =
          interactive && interactive.type === 'meal_intake_question' ? interactive : null;

        const created_at =
          last?.role === 'assistant' && (last.created_at || last.streaming)
            ? (last.created_at || new Date().toISOString())
            : new Date().toISOString();
        const patch = {
          _id: Date.now(),
          content: full_text,
          streaming: false,
          choices: meal ? null : choices || null,
          interactive: interactive || undefined,
          type: meal ? 'meal_intake_question' : undefined,
          message_type: meal ? 'meal_intake_question' : (choices ? 'question' : undefined),
          created_at,
        };

        // Normal path: streaming assistant bubble
        if (last?.streaming) {
          updated[updated.length - 1] = { ...last, ...patch };
          return updated;
        }

        // No streaming tokens: append a fresh assistant message.
        if (last?.role === 'user') {
          updated.push({
            _id: Date.now(),
            role: 'assistant',
            ...patch,
          });
          return updated;
        }

        // Last is assistant but streaming flag missing — still merge (meal form + LLM)
        if (last?.role === 'assistant') {
          updated[updated.length - 1] = { ...last, ...patch };
          return updated;
        }

        updated.push({
          _id: Date.now(),
          role: 'assistant',
          ...patch,
        });
        return updated;
      });
      setQuickReply(interactive?.type === 'meal_intake_question' ? null : choices || null);
      setSending(false);
      setAiTyping(false);
      scrollToBottom();
      markAiChatReadOnServer();
    });

    /** Backup if `done` payload loses `interactive` over the wire — server re-sends for meal form */
    sock.on('ai_chat:interactive_attach', ({ interactive: attach }) => {
      if (!attach?.items?.length) return;
      setMessages((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last?.role !== 'assistant') return prev;
        updated[updated.length - 1] = {
          ...last,
          streaming: false,
          interactive: attach,
          type: 'meal_intake_question',
          message_type: 'meal_intake_question',
        };
        return updated;
      });
    });

    sock.on('ai_chat:error', ({ message, code }) => {
      if (code === AI_MEMBERSHIP_REQUIRED_CODE) {
        setShowRenewalDialog(true);
        refreshUser?.();
      }
      toast.error(message);
      setSending(false);
      setAiTyping(false);
    });

    sock.on('ai_chat:meal_logged', ({ food_name, estimated_calories, meal_time_name, food_image_url }) => {
      // Add inline meal card into the chat
      setMessages(prev => [...prev, {
        _id: `meal-${Date.now()}`,
        type: 'meal_log',
        food_name,
        estimated_calories,
        meal_time_name,
        food_image_url: food_image_url || null,
        created_at: new Date().toISOString(),
      }]);
      // Also refresh today's total
      fetchTodayIntake();
      scrollToBottom();
    });

    socketRef.current = sock;
    return () => sock.disconnect();
  }, [
    token,
    scrollToBottom,
    refreshUser,
    markAiChatReadOnServer,
    fetchTodayIntake,
    loadAiSession,
  ]);

  // ── TTS ───────────────────────────────────────────────────────────────────
  const handlePlay = useCallback(async (msg) => {
    browserSpeakStop();
    if (audioEl) { audioEl.pause(); audioEl.src = ''; }
    setPlayingId(null);
    const text = msg.content;
    setPlayingId(msg._id);
    if (serverTts) {
      try {
        const res = await fetch(`${API_BASE_URL}/customer/ai-chat/tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ text }),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          if (res.status === 403 && errData?.code === AI_MEMBERSHIP_REQUIRED_CODE) {
            setShowRenewalDialog(true);
            refreshUser?.();
          }
          setPlayingId(null);
          throw new Error('TTS failed');
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.onended = () => { setPlayingId(null); URL.revokeObjectURL(url); };
        audio.onerror = () => setPlayingId(null);
        setAudioEl(audio);
        audio.play();
      } catch {
        browserSpeak(text, () => setPlayingId(null));
      }
    } else {
      browserSpeak(text, () => setPlayingId(null));
    }
  }, [serverTts, audioEl, token, refreshUser]);

  const handleStop = useCallback(() => {
    browserSpeakStop();
    if (audioEl) { audioEl.pause(); audioEl.src = ''; setAudioEl(null); }
    setPlayingId(null);
  }, [audioEl]);

  // ── STT ───────────────────────────────────────────────────────────────────
  const handleMicClick = useCallback(async () => {
    if (!aiMembershipActive) {
      openRenewalDialog();
      return;
    }
    if (micState === 'recording') {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
        setMicState('idle');
        return;
      }
      if (mediaRecorderRef.current) { mediaRecorderRef.current.stop(); return; }
      setMicState('idle');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition && !serverStt) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = BROWSER_STT_LANGUAGE;
      recognition.onresult = (e) => {
        const transcript = e.results[0][0].transcript;
        setInput(prev => prev + (prev ? ' ' : '') + transcript);
        setMicState('idle');
        inputRef.current?.focus();
      };
      recognition.onerror = () => setMicState('idle');
      recognition.onend = () => setMicState('idle');
      recognitionRef.current = recognition;
      recognition.start();
      setMicState('recording');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setMicState('transcribing');
        try {
          const blob = new Blob(audioChunksRef.current, { type: mimeType });
          const formData = new FormData();
          formData.append('audio', blob, `recording.${mimeType.includes('webm') ? 'webm' : 'mp4'}`);
          const res = await fetch(`${API_BASE_URL}/customer/ai-chat/stt`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          });
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            if (res.status === 403 && errData?.code === AI_MEMBERSHIP_REQUIRED_CODE) {
              setShowRenewalDialog(true);
              refreshUser?.();
            }
            throw new Error(errData.message || `STT error ${res.status}`);
          }
          const data = await res.json();
          if (data.text) {
            setInput(prev => prev + (prev ? ' ' : '') + data.text);
            inputRef.current?.focus();
          }
        } catch (err) { toast.error(err.message || 'Transcription failed'); }
        setMicState('idle');
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setMicState('recording');
    } catch { toast.error('Microphone access denied'); setMicState('idle'); }
  }, [micState, serverStt, token, aiMembershipActive, openRenewalDialog, refreshUser]);

  // ── Send message ──────────────────────────────────────────────────────────
  const handleSend = useCallback(() => {
    if (pendingImage) {
      sendUserText(input, { image: pendingImage });
    } else {
      sendUserText(input);
    }
  }, [input, sendUserText, pendingImage]);

  const handlePickImage = useCallback(() => {
    if (!aiMembershipActive) {
      openRenewalDialog();
      return;
    }
    imageFileInputRef.current?.click();
  }, [aiMembershipActive, openRenewalDialog]);

  const onImageFileChange = useCallback(async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      e.target.value = '';
      return;
    }
    try {
      const out = await compressImageFile(f);
      setPendingImage(out);
    } catch (err) {
      toast.error(err?.message || 'Could not process image');
    }
    e.target.value = '';
  }, []);

  const handleChoice = useCallback((choice, meta) => {
    if (choice === '__SKIP_ITEM__') {
      const fl = String(meta?.food_label || 'that item').trim() || 'that item';
      sendUserText(
        `I will not have ${fl}. Please exclude it completely from the meal and nutrition estimate.`,
      );
      return;
    }
    let text = String(choice || '').trim();
    if (!text) return;

    if (text.toLowerCase() === 'other' && meta?.allow_other) {
      const ph = meta?.other_placeholder || 'Type the portion (e.g. 180g, 2 roti, 330ml)';
      const v = window.prompt(ph);
      if (!v) return;
      text = String(v).trim();
      if (!text) return;
    }

    sendUserText(text);
  }, [sendUserText]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!aiMembershipActive) {
        openRenewalDialog();
        return;
      }
      handleSend();
    }
  };

  // ── Direct meal log ───────────────────────────────────────────────────────
  const handleDirectLog = useCallback(async ({ food_name, estimated_calories, meal_time_id, meal_time_name }) => {
    if (!aiMembershipActive) {
      openRenewalDialog();
      return;
    }
    setLoggingMeal(true);
    try {
      const res = await fetch(`${API_BASE_URL}/customer/ai-chat/log-meal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          food_name,
          estimated_calories,
          meal_time_id,
          meal_time_name,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });
      const data = await res.json();
      if (res.status === 403 && data?.code === AI_MEMBERSHIP_REQUIRED_CODE) {
        setShowRenewalDialog(true);
        refreshUser?.();
        return;
      }
      if (!res.ok) throw new Error(data.message || 'Failed to log meal');

      // Add inline card
      setMessages(prev => [...prev, {
        _id: `meal-${Date.now()}`,
        type: 'meal_log',
        food_name,
        estimated_calories,
        meal_time_name,
        created_at: new Date().toISOString(),
      }]);
      fetchTodayIntake();
      scrollToBottom();
      setShowLogForm(false);
      toast.success(`Logged: ${food_name}`);
    } catch (err) {
      toast.error(err.message || 'Failed to log meal');
    } finally {
      setLoggingMeal(false);
    }
  }, [token, fetchTodayIntake, scrollToBottom, aiMembershipActive, openRenewalDialog, refreshUser]);

  const isEmpty = messages.length === 0;
  const QUICK_SUGGESTIONS = [
    'I just ate 2 chapati',
    'Log my lunch as grilled chicken',
    'What should I eat for dinner?',
    'How many calories in a banana?',
    'Give me a 7-day meal plan',
    "What's my calorie balance today?",
  ];

  return (
    <CustomerLayout>
      <div className="h-full flex flex-col bg-muted">

        {/* Header */}
        <div className="bg-card border-b border-border px-4 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
              <Brain className="w-4 h-4 text-violet-600" />
            </div>
            <div>
              <div className="font-semibold text-sm text-foreground">AI Assistant</div>
              <div className="flex items-center gap-1.5">
                <span className={cn('w-1.5 h-1.5 rounded-full', connected ? 'bg-emerald-500' : 'bg-rose-400')} />
                <span className="text-xs text-muted-foreground">{connected ? 'Online' : 'Connecting...'}</span>
                {serverTts && <span className="text-[10px] bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded-full">TTS</span>}
                {serverStt && <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full">STT</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Today's log panel trigger */}
            <div className="relative">
              <button
                onClick={() => { setShowTodayPanel(v => !v); fetchTodayIntake(); }}
                data-testid="today-log-btn"
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground px-2.5 py-1.5 rounded-md hover:bg-muted transition-colors"
              >
                <UtensilsCrossed className="w-3.5 h-3.5" />
                Today
              </button>
              {showTodayPanel && (
                <TodayPanel items={todayItems} onClose={() => setShowTodayPanel(false)} />
              )}
            </div>

          </div>
        </div>

        {/* Calorie progress bar */}
        <CalorieBar
          consumed={todayConsumed}
          target={todayTarget}
          onRefresh={fetchTodayIntake}
        />

        <div className="flex flex-1 min-h-0">
          <div className="flex-1 flex flex-col min-w-0">
            {/* Welcome screen */}
            {isEmpty && !aiTyping && (
              <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center mb-4">
                  <Brain className="w-8 h-8 text-violet-600" />
                </div>
                <h2 className="text-lg font-semibold text-foreground mb-2">Your nutrition AI is ready</h2>
                <p className="text-sm text-muted-foreground max-w-sm mb-6">
                  Ask about your diet, log meals, track calories, or get personalized advice.
                </p>

                {/* Quick log bar */}
                <div className="w-full max-w-md mb-4">
                  <button
                    onClick={() => {
                      if (!aiMembershipActive) {
                        openRenewalDialog();
                        return;
                      }
                      setShowLogForm(true);
                    }}
                    data-testid="quick-log-meal-btn"
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 font-medium text-sm hover:bg-emerald-100 transition-colors"
                  >
                    <UtensilsCrossed className="w-4 h-4" />
                    Log a meal
                  </button>
                </div>

                <div className="flex flex-wrap gap-2 justify-center max-w-md">
                  {QUICK_SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        if (!aiMembershipActive) {
                          openRenewalDialog();
                          return;
                        }
                        setInput(s);
                        inputRef.current?.focus();
                      }}
                      className="text-xs bg-card border border-border text-foreground px-3 py-1.5 rounded-full hover:bg-muted transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            {(!isEmpty || aiTyping) && (
              <div className="app-modal-body flex-1 overflow-y-auto min-h-0 p-4">
                {messages.map((msg, i) => (
                  shouldHideAdjacentMealLogAck(msg, messages[i - 1], messages[i + 1]) ? null : (
                    <MessageBubble
                      key={msg._id || i}
                      msg={msg}
                      onPlay={handlePlay}
                      onStop={handleStop}
                      playingId={playingId}
                      onChoice={handleChoice}
                      onSubmitPortions={handleSubmitPortions}
                      portionFormLocked={
                        !!(Array.isArray(msg.interactive?.items) && msg.interactive.items.length > 0) &&
                        i < messages.length - 1
                      }
                      sending={sending}
                      authToken={token}
                      existingAccountRating={user?.app_rating}
                      onAppRated={refreshUser}
                    />
                  )
                ))}
                {aiTyping && <TypingIndicator />}
                <div ref={bottomRef} />
              </div>
            )}

            {/* Quick Log Form — slides up above the input */}
            {showLogForm && (
              <QuickLogForm
                mealTimes={mealTimes}
                onLog={handleDirectLog}
                onCancel={() => setShowLogForm(false)}
                loading={loggingMeal}
              />
            )}

            {/* Input area */}
            <div className="border-t border-border bg-card shrink-0">
              {quickReply?.choices?.length > 0 && (
                <QuickReplyBar
                  meta={quickReply}
                  onSend={(c) => {
                    if (c === '__SKIP_ITEM__') handleChoice('__SKIP_ITEM__', quickReply);
                    else sendUserText(c);
                  }}
                  disabled={sending || !connected}
                />
              )}
              <div className="px-3 pt-3 pb-2">
              <input
                ref={imageFileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onImageFileChange}
                aria-hidden
              />
              {pendingImage?.previewDataUrl ? (
                <div className="mb-2 flex items-start gap-2 p-2 rounded-xl bg-muted/50 border border-border">
                  <img
                    src={pendingImage.previewDataUrl}
                    alt="Selected meal"
                    className="h-20 w-20 object-cover rounded-lg border border-border shrink-0"
                  />
                  <div className="flex-1 min-w-0 text-xs text-muted-foreground">
                    <p className="font-medium text-foreground">Photo ready to send</p>
                    <p>Add an optional note below, then send.</p>
                    <button
                      type="button"
                      onClick={() => { setPendingImage(null); if (imageFileInputRef.current) imageFileInputRef.current.value = ''; }}
                      className="mt-1 text-primary hover:underline"
                    >
                      Remove photo
                    </button>
                  </div>
                </div>
              ) : null}
              <div className="flex gap-2 items-end">
                <MicButton state={micState} onClick={handleMicClick} />
                <button
                  type="button"
                  onClick={handlePickImage}
                  disabled={!aiMembershipActive || sending || !connected}
                  title="Add meal photo"
                  data-testid="ai-chat-image-btn"
                  className="w-10 h-10 rounded-xl border border-border bg-card text-muted-foreground flex items-center justify-center hover:bg-muted transition-colors disabled:opacity-40 shrink-0"
                >
                  <ImagePlus className="w-4 h-4" />
                </button>
                <textarea
                  ref={inputRef}
                  value={input}
                  readOnly={!aiMembershipActive}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={(e) => {
                    if (!aiMembershipActive) {
                      e.target.blur();
                      openRenewalDialog();
                    }
                  }}
                  onClick={() => {
                    if (!aiMembershipActive) openRenewalDialog();
                  }}
                  placeholder={
                    micState === 'recording'
                      ? 'Listening...'
                      : !aiMembershipActive
                        ? 'Tap to renew AI membership…'
                        : pendingImage
                          ? 'Optional note (e.g. I had half)…'
                          : quickReply?.choices?.length
                            ? 'Optional: type a different answer…'
                            : 'Ask about your diet or send a food photo…'
                  }
                  rows={1}
                  disabled={sending}
                  data-testid="ai-chat-input"
                  className={cn(
                    'flex-1 resize-none rounded-xl border border-border bg-muted px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 min-h-[42px] max-h-32 disabled:opacity-60',
                    !aiMembershipActive && 'cursor-pointer text-muted-foreground'
                  )}
                  style={{ scrollbarWidth: 'none' }}
                  onInput={(e) => {
                    const t = e.target;
                    t.style.height = 'auto';
                    t.style.height = Math.min(t.scrollHeight, 128) + 'px';
                  }}
                />
                <button
                  onClick={handleSend}
                  disabled={(!input.trim() && !pendingImage) || sending || !connected}
                  data-testid="ai-chat-send-btn"
                  className="w-10 h-10 rounded-xl bg-primary text-white flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>

              {/* Bottom action row */}
              <div className="flex items-center justify-between mt-1.5 px-1">
                <p className="text-[11px] text-muted-foreground">
                  Enter to send · Shift+Enter for newline
                  {micState === 'recording' ? ' · Click mic to stop' : ''}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (!aiMembershipActive) {
                      openRenewalDialog();
                      return;
                    }
                    setShowLogForm((v) => !v);
                  }}
                  data-testid="log-meal-toggle-btn"
                  className={cn(
                    'flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition-all',
                    showLogForm
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-transparent border-border text-muted-foreground hover:border-emerald-300 hover:text-emerald-700'
                  )}
                >
                  <UtensilsCrossed className="w-3 h-3" />
                  {showLogForm ? 'Close' : 'Log Meal'}
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AiMembershipRenewalDialog
        open={showRenewalDialog}
        onOpenChange={setShowRenewalDialog}
        expiryDate={user?.ai_plan_expiry}
        websiteUrl={branding?.website}
      />
    </CustomerLayout>
  );
}
